import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { useRoute, useLocation, Link } from "wouter"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { trpc } from "@/utils/trpc"
import { useAuth } from "@/utils/useAuth"
import {
  saveCommentDraft,
  getCommentDraft,
  clearCommentDraft,
  hasCommentDraft,
  debounce
} from "@/utils/draftStorage"
import {
  Loader2,
  ArrowLeft,
  Clock,
  Send,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Ticket,
  Inbox,
  Eye,
  RotateCcw,
  UserPlus,
  Play,
  Pause,
  Shield,
  GitMerge,
  MoreHorizontal,
  Search,
  Trash2,
  Zap,
  ChevronDown,
  Paperclip,
  AtSign,
  Smile,
  Heart,
  MessageSquare,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/utils/utils"
import usePermissions from "@/hooks/usePermissions"
import { MentionAutocomplete, type Mentionable } from "@/components/MentionAutocomplete"

const AVATAR_COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#06b6d4"]

function avatarColor(name: string) {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}
function initials(name: string) {
  const p = name.trim().split(/\s+/)
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?"
}

function Avatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-bold text-white flex-shrink-0"
      style={{ background: avatarColor(name), width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials(name)}
    </span>
  )
}

function StatusBadge({ status, statusConfig }: { status: string; statusConfig: any }) {
  const cfg = statusConfig[status] ?? statusConfig.new
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.text }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  )
}

function PriorityTag({ priority, priorityConfig }: { priority: string; priorityConfig: any }) {
  const cfg = priorityConfig[priority] ?? priorityConfig.p3
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap" style={{ color: cfg.color }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 22h20L12 2z" />
      </svg>
      {cfg.label}
    </span>
  )
}

function SlaStrip({ ticket, t }: { ticket: any; t: any }) {
  const { data: slaStatus } = trpc.tickets.getSlaStatus.useQuery(
    { ticketId: ticket.id },
    { refetchInterval: 30_000 },
  )

  if (!slaStatus || slaStatus.total === 0) return null

  const { elapsed, total, paused, deadlineAt, remaining } = slaStatus
  const breached = remaining <= 0
  const percentExact = total > 0 ? (elapsed / total) * 100 : 0
  const percentDisplay = Math.max(0, Math.min(100, Math.round(percentExact)))

  const tone = breached
    ? { text: "text-[#b91c1c]", bg: "bg-[#fef2f2]", border: "border-[#fecaca]", bar: "#ef4444" }
    : percentExact >= 80
      ? { text: "text-[#92400e]", bg: "bg-[#fffbeb]", border: "border-[#fde68a]", bar: "#f59e0b" }
      : { text: "text-[#065f46]", bg: "bg-[#ecfdf5]", border: "border-[#a7f3d0]", bar: "#10b981" }
  const barWidth = breached ? 100 : Math.min(percentExact, 100)

  function formatMs(ms: number): string {
    const absMs = Math.abs(ms)
    const days = Math.floor(absMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor((absMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((absMs % (1000 * 60)) / 1000)
    if (days > 0) return `${days}d ${hours}h ${minutes}m`
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
    if (minutes > 0) return `${minutes}m ${seconds}s`
    return `${seconds}s`
  }

  return (
    <div className={cn("rounded-lg border p-5 mb-6", tone.bg, tone.border)}>
      <div className={cn("text-[11px] font-bold uppercase tracking-wide opacity-70 mb-3", tone.text)}>
        {t("tickets.sla.serviceLevel")}
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className={cn("h-5 w-5", tone.text)} />
          <span className={cn("text-[13px] font-semibold", tone.text)}>
            {t("tickets.sla.slaStatus")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {paused && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#f3f4f6] text-[#6b7280]">
              <Pause className="h-3 w-3" /> {t("tickets.sla.paused")}
            </span>
          )}
          {breached && (
            <span className={cn("text-[10px] font-bold px-2.5 py-1 rounded-full", tone.text)} style={{ background: tone.bar + "20" }}>
              {t("tickets.sla.breached")}
            </span>
          )}
        </div>
      </div>
      <div className="mb-4">
        <span className={cn("text-[13px] font-semibold", tone.text)}>
          {breached ? (
            <>
              <span className="block text-[11px] opacity-70 mb-1">{t("tickets.sla.overdueBy")}</span>
              +{formatMs(Math.abs(remaining))}
            </>
          ) : (
            <>
              <span className="block text-[11px] opacity-70 mb-1">{t("tickets.sla.timeRemaining")}</span>
              {formatMs(remaining)}
            </>
          )}
        </span>
      </div>
      <div className="mb-2">
        <div className="w-full bg-white/60 rounded-full h-3 overflow-hidden shadow-sm">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${barWidth}%`, background: tone.bar }}
          />
        </div>
      </div>
      <div className={cn("flex items-center justify-between text-[10px] font-medium mb-4", tone.text)}>
        <span>{percentDisplay}% {t("tickets.sla.percentElapsed")}</span>
        <span>{100 - percentDisplay > 0 ? `${100 - percentDisplay}% ${t("tickets.sla.percentRemaining")}` : t("tickets.sla.slaExceeded")}</span>
      </div>
      <div className="border-t border-current/10 my-3" />
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <span className={cn("text-[10px] opacity-60", tone.text)}>{t("tickets.sla.elapsed")}</span>
          <span className={cn("text-[11px] font-mono font-bold", tone.text)}>{formatMs(elapsed)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className={cn("text-[10px] opacity-60", tone.text)}>{breached ? t("tickets.sla.breached") : t("tickets.sla.percentRemaining")}</span>
          <span className={cn("text-[11px] font-mono font-bold", tone.text)}>{formatMs(Math.abs(remaining))}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className={cn("text-[10px] opacity-60", tone.text)}>{t("tickets.sla.deadline")}</span>
          <span className={cn("text-[11px] font-mono font-bold", tone.text)}>
            {deadlineAt
              ? new Date(deadlineAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
              : "—"}
          </span>
        </div>
      </div>
    </div>
  )
}

function dayLabel(ts: number, t: any): string {
  const d = new Date(ts)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const isToday = d.toDateString() === today.toDateString()
  const isYesterday = d.toDateString() === yesterday.toDateString()

  if (isToday) return t("tickets.today")
  if (isYesterday) return t("tickets.yesterday")

  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function humanizeEvent(desc: string): string {
  if (!desc) return "updated";
  if (/[A-Z ]/.test(desc)) return desc;
  return desc.replace(/_/g, " ").replace(/^\w/, (ch) => ch.toUpperCase());
}

const EVENT_ICON: Record<string, { icon: any; color: string }> = {
  ticket_created: { icon: Ticket, color: "#3730a3" },
  assigned: { icon: UserPlus, color: "#1e40af" },
  status_changed: { icon: RotateCcw, color: "#92400e" },
  priority_changed: { icon: AlertTriangle, color: "#9a3412" },
  sla_warning: { icon: Zap, color: "#b45309" },
  sla_breach: { icon: Zap, color: "#b91c1c" },
  escalation: { icon: AlertTriangle, color: "#b91c1c" },
  ticket_merged: { icon: GitMerge, color: "#6d28d9" },
  ticket_reopened: { icon: RotateCcw, color: "#9f1239" },
};

function ConversationThread({ ticket }: { ticket: any }) {
  const { t } = useTranslation()
  const comments = (ticket.comments ?? []) as any[]
  const reporterId = ticket.reporterId

  if (comments.length === 0) {
    return (
      <div>
        <h3 className="text-[15px] font-bold text-[#1a1530] mb-4">Conversation</h3>
        <p className="text-[13px] text-[#8b84a3] text-center py-8">
          No conversation yet — start by replying below.
        </p>
      </div>
    )
  }

  // Group comments by day
  const groups: { label: string; comments: any[] }[] = []
  for (const c of comments) {
    const ts = new Date(c.createdAt).getTime()
    const label = dayLabel(ts, t)
    const last = groups[groups.length - 1]
    if (!last || last.label !== label) groups.push({ label, comments: [c] })
    else last.comments.push(c)
  }

  return (
    <div>
      <h3 className="text-[15px] font-bold text-[#1a1530] mb-4">
        Conversation
        <span className="ml-2 text-[12px] font-semibold text-[#8b84a3]">
          {comments.length} message{comments.length !== 1 ? "s" : ""}
        </span>
      </h3>

      {groups.map((g) => (
        <div key={g.label}>
          <div className="flex items-center gap-2.5 my-4 text-[11px] font-bold uppercase tracking-wider text-[#8b84a3]">
            <span className="flex-1 h-px bg-[#ece9f5]" />
            {g.label}
            <span className="flex-1 h-px bg-[#ece9f5]" />
          </div>
          {g.comments.map((c) => (
            <CommentBubble key={`c-${c.id}`} c={c} reporterId={reporterId} />
          ))}
        </div>
      ))}
    </div>
  );
}

function CommentBubble({ c, reporterId }: { c: any; reporterId: number }) {
  const internal = !!c.isInternal;
  const inbound = c.authorId === reporterId && !internal;
  return (
    <div className={cn("flex gap-3 mb-4 max-w-[85%]", inbound ? "" : "ml-auto flex-row-reverse")}>
      <Avatar name={c.authorName ?? "?"} size={32} />
      <div
        className={cn(
          "px-4 py-2.5 border rounded-2xl",
          inbound
            ? "bg-[#f6f5fb] border-[#ece9f5] rounded-tl-sm"
            : internal
              ? "bg-[#fffbeb] border-[#fcd34d] rounded-tr-sm"
              : "bg-[#f5f3ff] border-[#ddd6fe] rounded-tr-sm",
        )}
      >
        <div className="flex items-center gap-2 mb-1 flex-wrap text-[12px] text-[#6b6386]">
          <b className="text-[#1a1530] text-[13px]">{c.authorName ?? "Unknown"}</b>
          <span>{inbound ? "Reporter" : "Agent"}</span>
          <span>
            · {new Date(c.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
          {internal && (
            <span className="text-[9px] font-bold uppercase tracking-wide text-[#92400e] bg-[#fef3c7] px-1.5 py-0.5 rounded">
              Internal
            </span>
          )}
          {c.isOriginal && (
            <span className="text-[9px] font-bold uppercase tracking-wide text-[#6d28d9] bg-[#ede9fe] px-1.5 py-0.5 rounded">
              Original request
            </span>
          )}
        </div>
        <div className="text-[13.5px] text-[#3d3554] leading-relaxed whitespace-pre-wrap">{c.body}</div>
      </div>
    </div>
  );
}

function SystemDivider({ e }: { e: any }) {
  const vis = EVENT_ICON[e.type] ?? { icon: Clock, color: "#8b84a3" };
  const Icon = vis.icon;
  const time = new Date(e.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="flex items-center gap-2.5 my-2.5 text-[11.5px] text-[#8b84a3]">
      <span className="flex-1 h-px bg-[#ece9f5]" />
      <span className="flex items-center gap-1.5 px-1 text-center">
        <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: vis.color }} />
        {e.actorName && <b className="text-[#3d3554] font-semibold">{e.actorName}</b>}
        <span>{humanizeEvent(e.description)}</span>
        <span className="text-[#b8b2c9]">· {time}</span>
      </span>
      <span className="flex-1 h-px bg-[#ece9f5]" />
    </div>
  );
}
function ActivityStream({ ticket, t }: { ticket: any; t: any }) {
  const comments = Array.isArray(ticket.comments) ? [...ticket.comments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) : []
  const latestComment = comments[0]
  const assigneeName = (ticket.assignee as any)?.name

  const items = [
    ticket.createdAt && {
      icon: Ticket,
      label: t("tickets.created", "Created"),
      value: ticket.createdAt,
      note: `${t("tickets.reporter", "Reporter")}: ${(ticket.reporter as any)?.name ?? t("tickets.unknown", "Unknown")}`,
    },
    assigneeName && {
      icon: UserPlus,
      label: t("tickets.assigned", "Assigned"),
      value: ticket.updatedAt ?? ticket.createdAt,
      note: assigneeName,
    },
    latestComment && {
      icon: MessageSquare,
      label: latestComment.isInternal ? t("tickets.internalNote", "Internal note") : t("tickets.latestComment", "Latest comment"),
      value: latestComment.createdAt,
      note: (latestComment.body ?? "").trim().replace(/\s+/g, " ").slice(0, 72) || t("tickets.commentAdded", "Comment added"),
    },
    ticket.closedAt && {
      icon: CheckCircle2,
      label: t("tickets.closed", "Closed"),
      value: ticket.closedAt,
      note: t("tickets.statuses.closed", "Closed"),
    },
    ticket.resolvedAt && !ticket.closedAt && {
      icon: CheckCircle2,
      label: t("tickets.resolved", "Resolved"),
      value: ticket.resolvedAt,
      note: t("tickets.statuses.resolved", "Resolved"),
    },
  ].filter(Boolean).slice(0, 3) as Array<{ icon: any; label: string; value: string | Date; note: string }>

  if (items.length === 0) {
    return <div className="text-[12px] text-[#8b84a3]">No key activity yet.</div>
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => {
        const Icon = item.icon
        return (
          <div key={`${item.label}-${index}`} className="flex items-start gap-2 rounded-lg border border-[#ece9f5] bg-[#faf9ff] px-3 py-2">
            <div className="mt-0.5 rounded-full bg-white p-1.5 text-[#6d28d9]">
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] font-semibold text-[#1a1530]">{item.label}</span>
                <span className="text-[10px] text-[#8b84a3]">{new Date(item.value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <div className="text-[11px] text-[#6b6386] truncate">{item.note}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function TicketDetail() {
  const { t } = useTranslation()
  const [, setLocation] = useLocation()
  const [, params] = useRoute("/tickets/:id")
  const ticketId = params?.id ? Number(params.id) : null
  const { user: authUser } = useAuth({})
  const { hasPermission } = usePermissions()
  const canAssign = hasPermission("tickets.assign")
  const canDelete = hasPermission("tickets.admin")

  const STATUS_CONFIG = useMemo(() => ({
    new: { label: t("tickets.statuses.new"), bg: "#e0e7ff", text: "#3730a3", dot: "#6366f1" },
    assigned: { label: t("tickets.statuses.assigned"), bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
    inProgress: { label: t("tickets.statuses.inProgress"), bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
    pending: { label: t("tickets.statuses.pending"), bg: "#fce7f3", text: "#9f1239", dot: "#ec4899" },
    resolved: { label: t("tickets.statuses.resolved"), bg: "#d1fae5", text: "#065f46", dot: "#10b981" },
    closed: { label: t("tickets.statuses.closed"), bg: "#e5e7eb", text: "#374151", dot: "#6b7280" },
  }), [t])

  const PRIORITY_CONFIG = useMemo(() => ({
    p1: { short: "P1", label: t("tickets.priorities.p1"), color: "#dc2626" },
    p2: { short: "P2", label: t("tickets.priorities.p2"), color: "#ea580c" },
    p3: { short: "P3", label: t("tickets.priorities.p3"), color: "#0891b2" },
    p4: { short: "P4", label: t("tickets.priorities.p4"), color: "#65a30d" },
  }), [t])

  const [commentText, setCommentText] = useState("")
  const [isInternal, setIsInternal] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const queryClient = useQueryClient()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [expandedDescription, setExpandedDescription] = useState(false)
  const [isFollowing, setIsFollowing] = useState(false)
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [showCommentDraftIndicator, setShowCommentDraftIndicator] = useState(false)
  const [mentions, setMentions] = useState<Map<string, Mentionable>>(new Map())
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data: ticket, isLoading, refetch } = trpc.tickets.getById.useQuery(
    { id: ticketId! },
    { enabled: !!ticketId },
  )

  const { data: allUsers = { users: [] } } = trpc.users.list.useQuery(
    { limit: 1000, status: "active" },
  )

  useEffect(() => {
    if (!ticketId || draftLoaded) return

    const draft = getCommentDraft(ticketId)
    if (draft) {
      setCommentText(draft.body)
      setIsInternal(draft.isInternal)
      setShowCommentDraftIndicator(true)
    }
    setDraftLoaded(true)
  }, [ticketId, draftLoaded])

  const debouncedSaveCommentDraft = useCallback(
    debounce((text: string, internal: boolean) => {
      if (ticketId) {
        saveCommentDraft(ticketId, text, internal)
      }
    }, 500),
    [ticketId]
  )

  useEffect(() => {
    if (!draftLoaded || !ticketId) return
    debouncedSaveCommentDraft(commentText, isInternal)
  }, [commentText, isInternal, debouncedSaveCommentDraft, ticketId, draftLoaded])

  const addCommentMutation = trpc.tickets.addComment.useMutation({
    onSuccess: () => {
      if (ticketId) {
        clearCommentDraft(ticketId)
        console.log("[addCommentMutation] Success, calling refetch...");
        // Use a small delay to ensure the server has processed the comment
        setTimeout(() => {
          refetch();
        }, 500);
      }
      setCommentText("")
      setShowCommentDraftIndicator(false)
    },
    onError: (error) => {
      console.error("[Add Comment Error]", error.message);
      alert(`Failed to add comment: ${error.message}`);
    },
  })

  const changeStatusMutation = trpc.tickets.changeStatus.useMutation({
    onSuccess: () => refetch(),
    onError: (error) => {
      console.error("[Change Status Error]", error.message);
    },
  })

  const assignMutation = trpc.tickets.assign.useMutation({
    onSuccess: () => refetch(),
    onError: (error) => {
      console.error("[Assign Error]", error.message);
    },
  })

  const reopenMutation = trpc.tickets.reopen.useMutation({
    onSuccess: () => {
      setShowReopenModal(false)
      refetch()
    },
    onError: (error) => {
      console.error("[Reopen Error]", error.message);
    },
  })

  const mergeMutation = trpc.tickets.merge.useMutation({
    onSuccess: () => {
      setShowMergeModal(false)
      refetch()
    },
    onError: (error) => {
      console.error("[Merge Error]", error.message);
    },
  })

  const deleteMutation = trpc.tickets.delete.useMutation({
    onSuccess: () => {
      setShowDeleteModal(false)
      setLocation("/tickets")
    },
  })

  const { data: mergeHistory } = trpc.tickets.getMergeHistory.useQuery(
    { primaryTicketId: ticketId! },
    { enabled: !!ticketId },
  )

  const reopenWindowInfo = useMemo(() => {
    if (!ticket || ticket.status !== "closed" || !ticket.closedAt) return null

    const reopenWindowDays = (ticket.queue as any)?.reopenWindowDays ?? 7
    const closedAt = new Date(ticket.closedAt)
    const windowEnd = new Date(closedAt.getTime() + reopenWindowDays * 24 * 60 * 60 * 1000)
    const now = new Date()
    const isExpired = now > windowEnd
    const daysRemaining = Math.max(0, Math.ceil((windowEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)))

    return { isExpired, daysRemaining, reopenWindowDays }
  }, [ticket])

  if (!ticketId) {
    return <div className="py-10 text-center text-[#6B6B6B]">{t("tickets.unknown")}</div>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" />
      </div>
    )
  }

  if (!ticket) {
    return <div className="py-10 text-center text-[#6B6B6B]">{t("tickets.noTicketsFound")}</div>
  }

  const isMergedSource = !!ticket.mergedIntoId
  const isRequesterView = !canAssign && (ticket as any)?.reporterId === authUser?.id

  const handleMentionSelect = (mentionable: Mentionable, startPos: number, endPos: number) => {
    if (!textareaRef.current) return

    const text = textareaRef.current.value
    const beforeMention = text.substring(0, startPos)
    const afterMention = text.substring(endPos)
    const newText = `${beforeMention}@${mentionable.name} ${afterMention}`

    setCommentText(newText)
    setMentions(prev => new Map(prev).set(mentionable.id, mentionable))

    // Focus back to textarea and set cursor after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const cursorPos = startPos + mentionable.name.length + 2
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(cursorPos, cursorPos)
      }
    }, 0)
  }

  const handleAddComment = async () => {
    console.log("[handleAddComment] Starting...");
    console.log("[handleAddComment] commentText:", commentText);
    console.log("[handleAddComment] attachedFiles:", attachedFiles);
    console.log("[handleAddComment] ticket.id:", ticket?.id);

    if (!commentText.trim() && attachedFiles.length === 0) {
      console.log("[handleAddComment] Early return: no text or files");
      return;
    }

    console.log("[handleAddComment] Calling mutation...");
    addCommentMutation.mutate({
      ticketId: ticket.id,
      body: commentText,
      isInternal,
      attachments: [],
      mentions: Array.from(mentions.keys()).map(id => parseInt(id, 10)),
    });
    setAttachedFiles([])
    setMentions(new Map())
  }

  const handleAttachmentClick = () => {
    fileInputRef.current?.click()
  }


  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (files) {
      setAttachedFiles(prev => [...prev, ...Array.from(files)])
      e.currentTarget.value = ""
    }
  }

  const removeAttachment = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleStatusChange = (newStatus: string) => {
    changeStatusMutation.mutate({
      ticketId: ticket.id,
      newStatus: newStatus as any,
    })
  }

  const handleAssignToMe = () => {
    if (!authUser?.id) return
    assignMutation.mutate({
      ticketId: ticket.id,
      assigneeId: authUser.id as number,
    })
  }

  const handleReopen = (reason: string) => {
    reopenMutation.mutate({
      ticketId: ticket.id,
      reason,
    })
  }

  const handleMerge = (sourceIds: number[], reason: string) => {
    mergeMutation.mutate({
      primaryTicketId: ticket.id,
      sourceTicketIds: sourceIds,
      mergeReason: reason,
    })
  }

  const handleDelete = () => {
    deleteMutation.mutate({ ticketId: ticket.id })
  }

  const handleFollow = () => {
    setIsFollowing(!isFollowing)
  }

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white flex flex-col">
      <div className="bg-white border-b border-[#ece9f5] px-6 md:px-8 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => setLocation("/tickets/all")}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#6b6386] hover:bg-[#f5f3ff] hover:text-[#6d28d9] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 text-[13px] text-[#6b6386] font-medium">
          <span className="cursor-pointer hover:text-[#6d28d9]" onClick={() => setLocation("/tickets/all")}>
            {t("tickets.breadcrumbTickets")}
          </span>
          <span className="text-[#b8b2c9]">/</span>
          <span className="text-[#1a1530] font-semibold font-mono">{ticket.ticketNumber}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_320px] flex-1">
        <aside className="hidden lg:block bg-white border-r border-[#ece9f5] px-5 py-6 overflow-y-auto">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#6b6386] mb-4">{t("tickets.properties")}</div>
          <div className="mb-6">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3] mb-2">{t("tickets.requester")}</div>
            {(ticket.reporter as any)?.name ? (
              <div className="flex items-center gap-2">
                <Avatar name={(ticket.reporter as any).name} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-[#1a1530] truncate">{(ticket.reporter as any).name}</div>
                </div>
              </div>
            ) : (
              <span className="text-[12px] text-[#b8b2c9]">{t("tickets.unknown")}</span>
            )}
          </div>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3]">{t("tickets.assignee")}</div>
              {!((ticket.assignee as any)?.name) && canAssign && (
                <button
                  onClick={handleAssignToMe}
                  className="text-[10px] font-bold text-[#7c3aed] hover:text-[#6d28d9] whitespace-nowrap"
                >
                  {t("tickets.takeIt")}
                </button>
              )}
            </div>
            {(ticket.assignee as any)?.name ? (
              <div className="flex items-center gap-2">
                <Avatar name={(ticket.assignee as any).name} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-[#1a1530] truncate">{(ticket.assignee as any).name}</div>
                </div>
              </div>
            ) : (
              <span className="text-[12px] text-[#b8b2c9] italic">{t("tickets.unassigned")}</span>
            )}
          </div>
          <div className="mb-6">
            <div className="mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3]">{t("tickets.followers")}</div>
            </div>
            {ticket.watchers && ticket.watchers.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {ticket.watchers.slice(0, 3).map((w: any) => (
                  <div key={w.id} className="flex items-center gap-2 text-[11px]">
                    <Avatar name={w.userName ?? "?"} size={20} />
                    <span className="text-[#3d3554] truncate">{w.userName ?? t("tickets.unknown")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[11px] text-[#b8b2c9]">
                <input
                  type="text"
                  placeholder={t("tickets.addFollowersPlaceholder")}
                  className="w-full px-2 py-1.5 text-[11px] border border-[#ece9f5] rounded-md bg-white text-[#1a1530] placeholder-[#b8b2c9] focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
                />
              </div>
            )}
          </div>
          {(ticket.ticketType as any)?.name && (
            <div className="mb-6">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3] mb-2">{t("tickets.formLabel")}</div>
              <div className="text-[12px] text-[#3d3554] bg-white border border-[#ece9f5] rounded-md px-3 py-2">
                {(ticket.ticketType as any).name}
              </div>
            </div>
          )}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3]">{t("tickets.tags")}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {((ticket as any)?.labels as string[])?.length > 0 ? (
                <>
                  {((ticket as any).labels as string[]).map((label: string) => (
                    <span key={label} className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#ede9fe] text-[#6d28d9] text-[10px] font-semibold">
                      {label}
                    </span>
                  ))}
                  <button className="text-[10px] text-[#b8b2c9] hover:text-[#7c3aed] italic">{t("tickets.addTag")}</button>
                </>
              ) : (
                <button className="text-[10px] text-[#b8b2c9] hover:text-[#7c3aed]">{t("tickets.addTag")}</button>
              )}
            </div>
          </div>
          <div className="border-t border-[#ece9f5] pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3] mb-1.5">{t("tickets.type")}</div>
                <div className="text-[12px] text-[#1a1530]">Issue</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b84a3] mb-1.5">{t("tickets.priorityLabel")}</div>
                <div className="text-[12px]">
                  <PriorityTag priority={ticket.priority} priorityConfig={PRIORITY_CONFIG} />
                </div>
              </div>
            </div>
          </div>
        </aside>

        <div className="px-6 md:px-8 py-6 min-w-0">
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-mono text-[13px] text-[#6d28d9] font-bold">{ticket.ticketNumber}</span>
                <StatusBadge status={ticket.status} statusConfig={STATUS_CONFIG} />
                <PriorityTag priority={ticket.priority} priorityConfig={PRIORITY_CONFIG} />
                {(ticket.ticketType as any)?.name && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[11px] font-semibold bg-[#ede9fe] text-[#6d28d9]">
                    {(ticket.ticketType as any).name}
                  </span>
                )}
                {ticket.channel && ticket.channel !== "web" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[11px] font-semibold bg-[#f3f4f6] text-[#374151]">
                    via {ticket.channel.replace(/_/g, " ")}
                  </span>
                )}
                {ticket.reopenCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[11px] font-semibold bg-[#f3f4f6] text-[#374151]">
                    <RotateCcw className="h-3 w-3" /> {t("tickets.reopenBtn")} {ticket.reopenCount}×
                  </span>
                )}
              </div>
              {canAssign && (
                <div className="flex gap-2 flex-wrap justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-[13px] font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={changeStatusMutation.isPending}
                      >
                        {t("tickets.changeStatus")}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {
                        (
                          [
                            { s: "assigned", label: t("tickets.assignedStatus"), icon: Inbox },
                            { s: "inProgress", label: t("tickets.inProgressStatus"), icon: Play },
                            { s: "pending", label: t("tickets.pendingStatus"), icon: Pause },
                            { s: "resolved", label: t("tickets.resolvedStatus"), icon: CheckCircle2 },
                            { s: "closed", label: t("tickets.closedStatus"), icon: XCircle },
                          ] as const
                        )
                          .filter((o) => o.s !== ticket.status)
                          .map((o) => (
                            <DropdownMenuItem key={o.s} onClick={() => handleStatusChange(o.s)}>
                              <o.icon className="h-4 w-4 mr-2" /> {o.label}
                            </DropdownMenuItem>
                          ))
                      }
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#ece9f5] bg-white text-[13px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] hover:border-[#ddd6fe] hover:text-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowAssignModal(true)}
                    disabled={assignMutation.isPending}
                  >
                    <Inbox className="h-3.5 w-3.5" /> {t("tickets.assignBtn")}
                  </button>
                  {!isMergedSource && (
                    <button
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#ece9f5] bg-white text-[13px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] hover:border-[#ddd6fe] hover:text-[#6d28d9] transition-colors"
                      onClick={() => setShowMergeModal(true)}
                    >
                      <GitMerge className="h-3.5 w-3.5" /> {t("tickets.mergeBtn")}
                    </button>
                  )}
                  {!isMergedSource && (
                    <button
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#ece9f5] bg-white text-[13px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] hover:border-[#ddd6fe] hover:text-[#6d28d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setShowReopenModal(true)}
                      disabled={ticket.status !== "closed" || (reopenWindowInfo?.isExpired ?? false)}
                      title={
                        ticket.status !== "closed"
                          ? t("tickets.onlyClosedTicketsCanReopen")
                          : reopenWindowInfo?.isExpired
                            ? t("tickets.reopenWindowClosed", { days: reopenWindowInfo.reopenWindowDays })
                            : undefined
                      }
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> {t("tickets.reopenBtn")}
                    </button>
                  )}
                  {canDelete && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#ece9f5] bg-white text-[13px] font-semibold text-[#3d3554] hover:bg-[#f5f3ff] hover:border-[#ddd6fe] hover:text-[#6d28d9] transition-colors"
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleAssignToMe} disabled={assignMutation.isPending}>
                          <UserPlus className="h-4 w-4 mr-2" /> {t("tickets.assignToMe")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
            </div>
            <h1 className="text-2xl font-bold text-[#1a1530] leading-snug">{ticket.title}</h1>
          </div>

          {isMergedSource && (
            <div className="flex items-center gap-2.5 bg-[#fef3c7] border border-[#fcd34d] rounded-lg px-4 py-3 mb-6 text-[13px] text-[#92400e]">
              <GitMerge className="h-4 w-4 flex-shrink-0" />
              {t("tickets.mergedInto")}{" "}
              <button onClick={() => setLocation(`/tickets/${ticket.mergedIntoId}`)} className="font-bold underline">
                #{ticket.mergedIntoId}
              </button>
            </div>
          )}

          {ticket.description && (
            <div className="mb-6">
              <div className="mb-4">
                <h3 className="text-[14px] font-semibold text-[#1a1530] pb-3">{t("tickets.descriptionTitle")}</h3>
              </div>
              <div className={cn("text-[13.5px] text-[#3d3554] leading-relaxed whitespace-pre-wrap", !expandedDescription && "line-clamp-3")}>
                {ticket.description}
              </div>
              {ticket.description.split('\n').length > 3 && (
                <button
                  onClick={() => setExpandedDescription(!expandedDescription)}
                  className="block mt-3 text-[#6d28d9] font-semibold hover:text-[#7c3aed] text-[13px]"
                >
                  {expandedDescription ? t("tickets.showLess") : t("tickets.showMore")}
                </button>
              )}
            </div>
          )}

          {ticketId && <ConversationThread ticket={ticket} />}

          {!isMergedSource && ticket.status !== "closed" && (
            <div className="bg-white border border-[#ece9f5] rounded-xl overflow-hidden mt-4 shadow-[0_-4px_16px_rgba(76,29,149,0.06)] sticky bottom-3 z-10">
              {showCommentDraftIndicator && commentText.trim() && (
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#dbeafe] border-b border-[#bfdbfe] text-[#0369a1] text-[12px] font-semibold">
                  <Clock className="h-3.5 w-3.5" /> {t("tickets.draftAutoSaved")}
                </div>
              )}
              <div className="flex border-b border-[#ece9f5] bg-[#f5f3ff]">
                <button
                  onClick={() => setIsInternal(false)}
                  className={cn(
                    "px-4 py-2.5 text-[12.5px] font-semibold transition-colors",
                    !isInternal ? "bg-white text-[#6d28d9] shadow-[inset_0_-2px_0_#7c3aed]" : "text-[#6b6386]",
                  )}
                >
                  {t("tickets.officialResponse")}
                </button>
                {canAssign && (
                  <button
                    onClick={() => setIsInternal(true)}
                    className={cn(
                      "px-4 py-2.5 text-[12.5px] font-semibold transition-colors",
                      isInternal ? "bg-white text-[#6d28d9] shadow-[inset_0_-2px_0_#7c3aed]" : "text-[#6b6386]",
                    )}
                  >
                    {t("tickets.internalNote")}
                  </button>
                )}
              </div>
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={isInternal ? t("tickets.addInternalNotePlaceholder") : t("tickets.writeResponsePlaceholder")}
                  className="w-full px-4 py-3 text-[13px] text-[#1a1530] placeholder-[#b8b2c9] border-0 focus:outline-none resize-none"
                  rows={3}
                />
                {allUsers?.users && (
                  <MentionAutocomplete
                    textareaRef={textareaRef}
                    mentionables={allUsers.users.map((u: any) => ({
                      id: String(u.id),
                      name: u.name || u.email || "Unknown",
                      email: u.email,
                    }))}
                    onMentionSelect={handleMentionSelect}
                    value={commentText}
                  />
                )}
              </div>
              {attachedFiles.length > 0 && (
                <div className="px-4 py-2 bg-[#f5f3ff] border-t border-[#ece9f5]">
                  <div className="text-[11px] font-semibold text-[#6b6386] mb-2">{t("tickets.attachmentsLabel")} ({attachedFiles.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {attachedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-white border border-[#ece9f5] rounded text-[11px] text-[#3d3554]">
                        <span className="truncate max-w-[150px]">{file.name}</span>
                        <button
                          onClick={() => removeAttachment(idx)}
                          className="text-[#dc2626] hover:text-[#b91c1c] ml-1"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between px-4 py-3 bg-[#f5f3ff] border-t border-[#ece9f5]">
                <div className="flex gap-2">
                  <button
                    onClick={handleAttachmentClick}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#6b6386] hover:bg-white hover:text-[#6d28d9] transition-colors"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <button
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#6b6386] hover:bg-white hover:text-[#6d28d9] transition-colors"
                    title="Type @ to mention users"
                  >
                    <AtSign className="h-4 w-4" />
                  </button>
                  <button className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#6b6386] hover:bg-white hover:text-[#6d28d9] transition-colors">
                    <Smile className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={handleAddComment}
                  disabled={(!commentText.trim() && attachedFiles.length === 0) || addCommentMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-[13px] font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-3.5 w-3.5" /> {t("tickets.send")}
                </button>
              </div>
            </div>
          )}
        </div>

        <aside className="hidden lg:block bg-white border-t lg:border-t-0 lg:border-l border-[#ece9f5] px-5 py-6 overflow-y-auto">
          {!isRequesterView &&
            ticket.slaDeadlineAt &&
            !["closed", "resolved"].includes(ticket.status) && <SlaStrip ticket={ticket} t={t} />}

          <div className="mb-6">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#6b6386] mb-3">{t("tickets.activity")}</div>
            {ticketId && <ActivityStream ticket={ticket} t={t} />}
          </div>

          <div className="mb-6 pt-4 border-t border-[#ece9f5]">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#ece9f5]">
              <Avatar name={(ticket.reporter as any)?.name ?? "?"} size={40} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-[#1a1530]">{(ticket.reporter as any)?.name ?? t("tickets.unknown")}</div>
                <div className="text-[11px] text-[#8b84a3]">{t("tickets.reporter")}</div>
              </div>
            </div>
            <div className="space-y-3 text-[12px]">
              <div>
                <div className="text-[10px] font-semibold text-[#8b84a3] mb-0.5">{t("common.email")}</div>
                <div className="text-[#6d28d9] break-all">{(ticket.reporter as any)?.email ?? "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8b84a3] mb-0.5">{t("tickets.localTime")}</div>
                <div className="text-[#1a1530]">
                  {(ticket.reporter as any)?.localTime
                    ? new Date().toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8b84a3] mb-0.5">{t("tickets.language")}</div>
                <div className="text-[#1a1530]">{(ticket.reporter as any)?.language ?? "English (US)"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8b84a3] mb-0.5">{t("tickets.queue")}</div>
                <div className="text-[#1a1530]">{(ticket.queue as any)?.name ?? "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8b84a3] mb-0.5">{t("tickets.source")}</div>
                <div className="text-[#1a1530] capitalize">{(ticket.channel ?? "web").replace(/_/g, " ")}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8b84a3] mb-0.5">{t("tickets.created")}</div>
                <div className="text-[#1a1530]">
                  {ticket.createdAt
                    ? new Date(ticket.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6 pb-4 border-t border-[#ece9f5] pt-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#6b6386] mb-3">{t("tickets.interactionHistory")}</div>
            <div className="bg-[#f5f3ff] border border-[#ede9fe] rounded-lg px-3 py-2.5">
              <div className="text-[12px] text-[#1a1530] font-semibold">{ticket.title}</div>
              <div className="text-[11px] text-[#8b84a3] mt-1">
                {dayLabel(Date.now(), t)} · {new Date().toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="text-[11px] text-[#6b6386] mt-2">
                {t("common.status")} <span className="font-semibold text-[#1a1530]">{STATUS_CONFIG[ticket.status]?.label}</span>
              </div>
            </div>
          </div>

          {ticket.watchers && ticket.watchers.length > 0 && (
            <div className="pt-4 border-t border-[#ece9f5]">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#6b6386] mb-3">{t("tickets.watchers")}</div>
              <div className="flex flex-col gap-2">
                {ticket.watchers.map((w: any) => (
                  <div key={w.id} className="flex items-center gap-2 text-[12px]">
                    <Avatar name={w.userName ?? "?"} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[#1a1530] font-medium truncate">{w.userName ?? t("tickets.unknown")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(ticket.mergedIntoId || (ticket as any).securityAlertId || (ticket as any).approvalInstanceId) && (
            <div className="pt-4 border-t border-[#ece9f5]">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#6b6386] mb-3">{t("tickets.linked")}</div>
            </div>
          )}
        </aside>
      </div>

      {/* Merge Modal */}
      <MergeModal
        open={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        onConfirm={handleMerge}
        isPending={mergeMutation.isPending}
        primaryTicketId={ticket.id}
      />

      {/* Assign Modal */}
      <AssignModal
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onConfirm={(assigneeId) =>
          assignMutation.mutate(
            { ticketId: ticket.id, assigneeId },
            { onSuccess: () => setShowAssignModal(false) },
          )
        }
        isPending={assignMutation.isPending}
        defaultAssigneeId={(ticket.assignee as any)?.id ?? undefined}
      />
    </div>
  )
}

// ============================================================================
// MERGE MODAL
// ============================================================================

function MergeModal({
  open,
  onClose,
  onConfirm,
  isPending,
  primaryTicketId,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (sourceIds: number[], reason: string) => void;
  isPending: boolean;
  primaryTicketId: number;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<Record<number, any>>({});
  const [reason, setReason] = useState("");

  // Debounce the search input so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data: searchResults, isFetching } = trpc.tickets.getAll.useQuery(
    { search: debouncedQuery || undefined, limit: 20, offset: 0 },
    { enabled: open },
  );

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setDebouncedQuery("");
      setSelectedIds([]);
      setSelectedTickets({});
      setReason("");
    }
  }, [open]);

  // Server already filters by search text; just drop the primary and already-merged tickets
  const filteredResults = (searchResults?.tickets ?? []).filter(
    (t: any) => t.id !== primaryTicketId && !t.mergedIntoId,
  );

  const toggleSelect = (ticket: any) => {
    const id = ticket.id;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
    setSelectedTickets((prev) => ({ ...prev, [id]: ticket }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-indigo-500" />
            Merge Tickets
          </DialogTitle>
          <DialogDescription>
            Select tickets to merge into this one. Comments, attachments, and watchers will be migrated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
            <Input
              placeholder="Search by ticket # or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Search results — always shown while modal is open (recent tickets by default) */}
          <div className="max-h-56 overflow-y-auto space-y-1 border border-[#E0E0E0] rounded-md p-2">
            {!debouncedQuery && (
              <p className="text-[11px] text-[#9CA3AF] px-1 pb-1">Recent tickets</p>
            )}
            {isFetching && filteredResults.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] text-center py-3 flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
              </p>
            ) : filteredResults.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] text-center py-3">
                {debouncedQuery ? "No matching tickets" : "No other tickets available"}
              </p>
            ) : (
              filteredResults.map((t: any) => (
                <div
                  key={t.id}
                  onClick={() => toggleSelect(t)}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded cursor-pointer text-sm",
                    selectedIds.includes(t.id) ? "bg-indigo-50 border border-indigo-200" : "hover:bg-[#FAFAFA]",
                  )}
                >
                  <input type="checkbox" checked={selectedIds.includes(t.id)} readOnly className="rounded" />
                  <span className="font-mono text-[#5B2C93] text-xs shrink-0">{t.ticketNumber}</span>
                  <span className="text-[#2C2C2C] truncate">{t.title}</span>
                </div>
              ))
            )}
          </div>

          {/* Selected tickets */}
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedIds.map((id) => {
                const t = selectedTickets[id];
                return (
                  <Badge key={id} variant="outline" className="text-xs cursor-pointer" onClick={() => toggleSelect(t ?? { id })}>
                    {t?.ticketNumber ?? `#${id}`} ×
                  </Badge>
                );
              })}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-[#2C2C2C] block mb-1.5">
              Merge reason *
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are these tickets being merged?"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            onClick={() => onConfirm(selectedIds, reason)}
            disabled={selectedIds.length === 0 || !reason.trim() || isPending}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging...</>
            ) : (
              <><GitMerge className="h-4 w-4 mr-2" />Merge {selectedIds.length} ticket{selectedIds.length > 1 ? "s" : ""}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ASSIGN MODAL
// ============================================================================

function AssignModal({
  open,
  onClose,
  onConfirm,
  isPending,
  defaultAssigneeId,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (assigneeId: number) => void;
  isPending: boolean;
  defaultAssigneeId?: number;
}) {
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(defaultAssigneeId);

  const { data: usersData, isLoading } = trpc.users.list.useQuery(
    { search: search.trim() || undefined, status: "active", limit: 25, offset: 0 },
    { enabled: open },
  );
  const users = (usersData as any)?.users ?? [];

  useEffect(() => {
    if (open) setSelectedUserId(defaultAssigneeId);
  }, [open, defaultAssigneeId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Ticket</DialogTitle>
          <DialogDescription>Select an assignee for this ticket.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border border-[#E0E0E0] rounded-md">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#5B2C93]" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] text-center py-6">No users found</p>
            ) : (
              <div className="p-2 space-y-1">
                {users.map((u: any) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSelectedUserId(u.id)}
                    className={cn(
                      "w-full text-left flex items-center justify-between px-3 py-2 rounded-md text-sm",
                      selectedUserId === u.id ? "bg-[#F5F3FF] ring-1 ring-[#E8DCF5]" : "hover:bg-[#FAFAFA]",
                    )}
                  >
                    <span className="text-[#2C2C2C]">
                      {u.name ?? (`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || `User #${u.id}`)}
                    </span>
                    {selectedUserId === u.id && (
                      <Badge variant="outline" className="text-xs">Selected</Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            onClick={() => selectedUserId && onConfirm(selectedUserId)}
            disabled={!selectedUserId || isPending}
            className="bg-[#5B2C93] hover:bg-[#4A2377] text-white"
          >
            {isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Assigning...</>
            ) : (
              <><Inbox className="h-4 w-4 mr-2" />Assign</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
